import { useMutation } from '@apollo/client';
import { graphql } from '@gql/gql';
import type {
  MtAnimalBreederGetAnimalQuery,
  MtAnimalBreederUpdateAnimalMutation,
  MtAnimalBreederUpdateAnimalMutationVariables,
} from '@gql/graphql';
import type { MtAnimalsBreederPageFormValues } from '@presentation/pages';
import { useCallback } from 'react';
import { useAnimalUpdateBuilder } from './useAnimalUpdateBuilder';
import { MT_ANIMAL_BREEDER_GET_ANIMAL } from './useGetAnimal';

export const MT_ANIMAL_BREEDER_UPDATE_ANIMAL = graphql(`
  mutation MtAnimalBreederUpdateAnimal(
    $data: AnimalUpdateInput!
    $animalId: Int!
    $imageUpload: Upload
    $passportFileUpload: Upload
  ) {
    updateAnimal(
      data: $data
      animalId: $animalId
      imageUpload: $imageUpload
      passportFileUpload: $passportFileUpload
    ) {
      id
    }
  }
`);

export const useUpdateAnimal = () => {
  const [updateAnimal, { error, loading }] = useMutation<
    MtAnimalBreederUpdateAnimalMutation,
    MtAnimalBreederUpdateAnimalMutationVariables
  >(MT_ANIMAL_BREEDER_UPDATE_ANIMAL, {
    awaitRefetchQueries: true,
    refetchQueries: [MT_ANIMAL_BREEDER_GET_ANIMAL],
  });

  const { animalUpdateBuilder } = useAnimalUpdateBuilder();

  const updateAnimalFromForm = useCallback(
    (
      values: MtAnimalsBreederPageFormValues,
      animal?: NonNullable<MtAnimalBreederGetAnimalQuery['accountMe']>['animals'][number],
    ) => {
      if (animal == null) return;

      const { data } = animalUpdateBuilder(values);

      const imagesUpload: Scalar_Upload[] = values.images
        ?.map((file) => (typeof file === 'string' ? undefined : (file as Scalar_Upload)))
        .filter((file) => file != null) as Scalar_Upload[];

      return updateAnimal({
        variables: {
          data,
          animalId: animal.id,
          ...(imagesUpload?.[0] ? { imageUpload: imagesUpload[0] } : {}),
          ...(values.passportFile && typeof values.passportFile !== 'string'
            ? { passportFileUpload: values.passportFile }
            : {}),
        },
      });
    },
    [animalUpdateBuilder, updateAnimal],
  );

  return {
    updateAnimalLoading: loading,
    updateAnimalError: error,
    updateAnimalFromForm,
    updateAnimal,
  };
};
